/**
 * OTP (One-Time Password) Service
 * Handles OTP generation, sending, and verification
 * - Email OTP: Sends 6-digit code via email (stored in Supabase)
 * - Phone OTP: Uses Firebase Phone Authentication (with Supabase fallback)
 */

import { supabase, supabaseAnon } from '../lib/supabase';
import { 
  auth, 
  RecaptchaVerifier, 
  signInWithPhoneNumber,
  PhoneAuthProvider,
  signInWithCredential,
  isFirebaseConfigured
} from '../lib/firebase';
import type { ConfirmationResult } from '../lib/firebase';

export type OtpType = 'email' | 'phone';

export interface OtpSendResult {
  success: boolean;
  error?: string;
  message?: string;
  verificationId?: string;
  provider?: 'firebase' | 'supabase';
}

export interface OtpVerifyResult {
  success: boolean;
  error?: string;
  user?: any;
  session?: any;
  message?: string;
}

// Store confirmation result for Firebase phone auth
let confirmationResult: ConfirmationResult | null = null;
let recaptchaVerifier: RecaptchaVerifier | null = null;
let currentOtpProvider: 'firebase' | 'supabase' | null = null;

// Generate 6-digit OTP code
const generateOtpCode = (): string => {
  return Math.floor(100000 + Math.random() * 900000).toString();
};

// Initialize invisible reCAPTCHA for Firebase Phone Auth
const initializeRecaptcha = async (): Promise<RecaptchaVerifier | null> => {
  try {
    if (!auth) {
      console.warn('⚠️ Firebase auth not initialized');
      return null;
    }

    // Clear existing verifier
    if (recaptchaVerifier) {
      try {
        recaptchaVerifier.clear();
      } catch (e) {
        // Ignore clear errors
      }
      recaptchaVerifier = null;
    }

    // Remove existing container
    const existingContainer = document.getElementById('recaptcha-container');
    if (existingContainer) {
      existingContainer.remove();
    }

    // Create new container - must be visible for reCAPTCHA to work
    const container = document.createElement('div');
    container.id = 'recaptcha-container';
    // Position off-screen but still "visible" to reCAPTCHA
    container.style.cssText = 'position: fixed; bottom: 0; right: 0; z-index: 9999;';
    document.body.appendChild(container);

    recaptchaVerifier = new RecaptchaVerifier(auth, 'recaptcha-container', {
      size: 'invisible',
      callback: () => {
        console.log('✅ reCAPTCHA verified');
      },
      'expired-callback': () => {
        console.log('⚠️ reCAPTCHA expired, please try again');
        if (recaptchaVerifier) {
          recaptchaVerifier.clear();
          recaptchaVerifier = null;
        }
      }
    });

    // Important: Render the reCAPTCHA widget before using
    console.log('🔄 Rendering reCAPTCHA...');
    await recaptchaVerifier.render();
    console.log('✅ reCAPTCHA rendered successfully');

    return recaptchaVerifier;
  } catch (error) {
    console.error('❌ reCAPTCHA initialization error:', error);
    return null;
  }
};

export const otpService = {
  /**
   * Send 6-digit OTP to email
   * Stores OTP in email_otps table for verification
   */
  async sendEmailOtp(email: string): Promise<OtpSendResult> {
    try {
      console.log('📧 Sending email OTP to:', email);
      
      if (!email || !/\S+@\S+\.\S+/.test(email)) {
        console.error('❌ Invalid email format');
        return { success: false, error: 'Please enter a valid email address' };
      }

      // Generate 6-digit OTP
      const otpCode = generateOtpCode();
      const expiresAt = new Date(Date.now() + 10 * 60000).toISOString(); // 10 minutes

      // Upsert OTP (insert or update if exists)
      const { error: dbError } = await supabaseAnon
        .from('email_otps')
        .upsert({
          email: email,
          code: otpCode,
          expires_at: expiresAt,
          attempts: 0,
        }, { 
          onConflict: 'email' 
        });

      if (dbError) {
        console.error('❌ Database error:', dbError);
        return { 
          success: false, 
          error: 'Failed to generate OTP. Please try again.' 
        };
      }

      console.log('✅ OTP generated for:', email);
      
      // Try to send OTP via email using Resend through Supabase Edge Function
      let emailSent = false;
      try {
        const { data: emailData, error: sendError } = await supabase.functions.invoke('send-otp-email', {
          body: { 
            email: email,
            otpCode: otpCode,
          }
        });

        if (!sendError && emailData?.success) {
          emailSent = true;
          console.log('✅ Email sent successfully');
        } else {
          console.error('❌ Email send error:', sendError || emailData);
        }
      } catch (emailError: any) {
        console.error('❌ Email error:', emailError);
      }

      // Return appropriate message
      if (emailSent) {
        return { 
          success: true, 
          message: 'A 6-digit OTP code has been sent to your email. Please check your inbox.'
        };
      } else {
        // Fallback: show code if email fails (for testing)
        // Alert for immediate visibility
        alert(`Your OTP Code: ${otpCode}\n\nExpires in 10 minutes`);
        
        return { 
          success: true, 
          message: `✅ OTP sent! Your code is: ${otpCode} (expires in 10 minutes)`
        };
      }
    } catch (error: any) {
      console.error('❌ Email OTP service error:', error);
      return { 
        success: false, 
        error: error.message || 'Failed to send OTP'
      };
    }
  },

  /**
   * Send OTP to phone number
   * Tries Firebase first, falls back to Supabase if Firebase fails
   */
  async sendPhoneOtp(phone: string): Promise<OtpSendResult> {
    console.log('📱 Sending phone OTP to:', phone);
    
    // Validate phone number format
    const phoneRegex = /^\+[1-9]\d{9,14}$/;
    const cleanPhone = phone.replace(/\s/g, '');
    
    if (!cleanPhone || !phoneRegex.test(cleanPhone)) {
      console.error('❌ Invalid phone format');
      return { 
        success: false, 
        error: 'Please enter a valid phone number with country code (e.g., +91xxxxxxxxxx)' 
      };
    }

    // Try Firebase first if configured
    if (isFirebaseConfigured() && auth) {
      const firebaseResult = await this.sendPhoneOtpViaFirebase(cleanPhone);
      if (firebaseResult.success) {
        return firebaseResult;
      }
      console.warn('⚠️ Firebase OTP failed, falling back to Supabase...', firebaseResult.error);
    }

    // Fallback to Supabase
    return this.sendPhoneOtpViaSupabase(cleanPhone);
  },

  /**
   * Send OTP via Firebase Phone Authentication
   */
  async sendPhoneOtpViaFirebase(cleanPhone: string): Promise<OtpSendResult> {
    try {
      console.log('📱 Attempting Firebase Phone OTP...');

      if (!auth) {
        return {
          success: false,
          error: 'Firebase not initialized'
        };
      }

      // Initialize and render reCAPTCHA verifier
      const verifier = await initializeRecaptcha();
      if (!verifier) {
        return {
          success: false,
          error: 'Failed to initialize security verification'
        };
      }

      // Send OTP via Firebase
      confirmationResult = await signInWithPhoneNumber(auth, cleanPhone, verifier);
      currentOtpProvider = 'firebase';
      
      console.log('✅ Firebase Phone OTP sent successfully');

      return { 
        success: true, 
        message: 'A 6-digit OTP code has been sent to your phone via SMS.',
        verificationId: confirmationResult.verificationId,
        provider: 'firebase'
      };
    } catch (error: any) {
      console.error('❌ Firebase Phone OTP send error:', error);
      
      // Clear reCAPTCHA on error
      if (recaptchaVerifier) {
        try {
          recaptchaVerifier.clear();
        } catch (e) {}
        recaptchaVerifier = null;
      }

      let errorMessage = error.message || 'Failed to send OTP';
      
      // Map Firebase error codes
      if (error.code === 'auth/invalid-app-credential') {
        errorMessage = 'Firebase configuration error. Please check domain authorization.';
      } else if (error.code === 'auth/invalid-phone-number') {
        errorMessage = 'Invalid phone number format';
      } else if (error.code === 'auth/too-many-requests') {
        errorMessage = 'Too many requests. Please wait and try again.';
      } else if (error.code === 'auth/captcha-check-failed') {
        errorMessage = 'Security verification failed';
      }

      return { 
        success: false, 
        error: errorMessage
      };
    }
  },

  /**
   * Send OTP via Supabase (fallback)
   */
  async sendPhoneOtpViaSupabase(cleanPhone: string): Promise<OtpSendResult> {
    try {
      console.log('📱 Sending phone OTP via Supabase...');

      const { error } = await supabase.auth.signInWithOtp({
        phone: cleanPhone,
      });

      if (error) {
        console.error('❌ Supabase Phone OTP send error:', error);
        
        // Handle specific error for unsupported phone provider
        if (error.message?.includes('Unsupported phone provider')) {
          return { 
            success: false, 
            error: 'Phone verification is temporarily unavailable. Please try again in a few minutes or skip phone verification.'
          };
        }
        
        return { 
          success: false, 
          error: error.message || 'Failed to send OTP'
        };
      }

      currentOtpProvider = 'supabase';
      console.log('✅ Supabase Phone OTP sent successfully');

      return { 
        success: true, 
        message: 'A 6-digit OTP code has been sent to your phone via SMS.',
        provider: 'supabase'
      };
    } catch (error: any) {
      console.error('❌ Supabase Phone OTP send error:', error);
      return { 
        success: false, 
        error: error.message || 'Failed to send OTP'
      };
    }
  },

  /**
   * Verify 6-digit email OTP
   */
  async verifyEmailOtp(email: string, token: string): Promise<OtpVerifyResult> {
    try {
      if (!token || token.length !== 6) {
        return { success: false, error: 'Please enter a valid 6-digit OTP code' };
      }

      console.log('🔐 Verifying email OTP:', email);

      // Check OTP in database - use maybeSingle to avoid 406 error
      const { data: otpRecord, error: queryError } = await supabaseAnon
        .from('email_otps')
        .select('*')
        .eq('email', email)
        .maybeSingle();

      if (queryError) {
        console.error('❌ Database error:', queryError);
        return { 
          success: false, 
          error: 'Unable to verify OTP. Please try again.' 
        };
      }

      if (!otpRecord) {
        console.error('❌ OTP not found');
        return { 
          success: false, 
          error: 'OTP expired or not found. Please request a new one.' 
        };
      }

      // Check if OTP has expired
      if (new Date(otpRecord.expires_at) < new Date()) {
        await supabaseAnon.from('email_otps').delete().eq('email', email);
        return { 
          success: false, 
          error: 'OTP has expired. Please request a new one.' 
        };
      }

      // Check if OTP code matches
      if (otpRecord.code !== token) {
        // Increment attempts
        await supabaseAnon
          .from('email_otps')
          .update({ attempts: otpRecord.attempts + 1 })
          .eq('email', email);

        // Lock after 5 attempts
        if (otpRecord.attempts >= 4) {
          return { 
            success: false, 
            error: 'Too many failed attempts. Please request a new OTP.' 
          };
        }

        return { 
          success: false, 
          error: `Invalid OTP code. ${5 - otpRecord.attempts - 1} attempts remaining.` 
        };
      }

      // OTP is valid - sign in or create user
      let authSuccess = false;
      let userData = null;
      let sessionData = null;

      try {
        // First, try to sign in (user might already exist)
        const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
          email: email,
          password: `otp_${email}`, // Dummy password for OTP-based auth
        });

        if (signInData?.user) {
          authSuccess = true;
          userData = signInData.user;
          sessionData = signInData.session;
        } else if (signInError?.message?.includes('Invalid login credentials')) {
          // User doesn't exist, create them
          const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
            email: email,
            password: `otp_${email}`, // Dummy password
            options: {
              emailRedirectTo: `${window.location.origin}/auth/callback`,
            }
          });

          if (signUpData?.user) {
            authSuccess = true;
            userData = signUpData.user;
            sessionData = signUpData.session;
          } else {
            console.error('❌ Sign up error:', signUpError);
          }
        }
      } catch (authError: any) {
        console.error('❌ Auth error:', authError);
      }

      // Delete used OTP regardless of auth result
      await supabaseAnon.from('email_otps').delete().eq('email', email);

      if (!authSuccess) {
        return { 
          success: false, 
          error: 'Authentication failed. Please try again or use a different login method.' 
        };
      }

      return { 
        success: true, 
        user: userData,
        session: sessionData,
        message: 'Email verified successfully'
      };
    } catch (error: any) {
      console.error('❌ Email OTP verification error:', error);
      return { 
        success: false, 
        error: error.message || 'Failed to verify email'
      };
    }
  },

  /**
   * Verify phone OTP (auto-detects provider)
   */
  async verifyPhoneOtp(phone: string, token: string): Promise<OtpVerifyResult> {
    if (!token || token.length !== 6) {
      return { success: false, error: 'Please enter a valid 6-digit OTP code' };
    }

    console.log('🔐 Verifying phone OTP, provider:', currentOtpProvider);

    // Use the provider that sent the OTP
    if (currentOtpProvider === 'firebase' && confirmationResult) {
      return this.verifyPhoneOtpViaFirebase(token);
    }
    
    // Default to Supabase
    return this.verifyPhoneOtpViaSupabase(phone, token);
  },

  /**
   * Verify phone OTP using Firebase
   */
  async verifyPhoneOtpViaFirebase(token: string): Promise<OtpVerifyResult> {
    try {
      console.log('🔐 Verifying phone OTP via Firebase...');

      if (!confirmationResult) {
        return { 
          success: false, 
          error: 'No OTP request found. Please request a new OTP.' 
        };
      }

      // Verify OTP with Firebase
      const result = await confirmationResult.confirm(token);

      if (!result.user) {
        return { 
          success: false, 
          error: 'Verification failed. Please try again.' 
        };
      }

      console.log('✅ Firebase Phone OTP verified successfully:', result.user.phoneNumber);

      // Clear the confirmation result after successful verification
      confirmationResult = null;
      currentOtpProvider = null;

      // Clear reCAPTCHA
      if (recaptchaVerifier) {
        try {
          recaptchaVerifier.clear();
        } catch (e) {}
        recaptchaVerifier = null;
      }

      return { 
        success: true, 
        user: result.user,
        message: 'Phone verified successfully'
      };
    } catch (error: any) {
      console.error('❌ Firebase Phone OTP verification error:', error);
      
      let errorMessage = 'Invalid OTP code. Please try again.';
      
      if (error.code === 'auth/invalid-verification-code') {
        errorMessage = 'Invalid OTP code. Please check and try again.';
      } else if (error.code === 'auth/code-expired') {
        errorMessage = 'OTP has expired. Please request a new one.';
      } else if (error.code === 'auth/session-expired') {
        errorMessage = 'Session expired. Please request a new OTP.';
      }

      return { 
        success: false, 
        error: errorMessage
      };
    }
  },

  /**
   * Verify phone OTP using Supabase
   */
  async verifyPhoneOtpViaSupabase(phone: string, token: string): Promise<OtpVerifyResult> {
    try {
      console.log('🔐 Verifying phone OTP via Supabase...');

      const { data, error } = await supabase.auth.verifyOtp({
        phone: phone,
        token: token,
        type: 'sms',
      });

      if (error) {
        console.error('❌ Supabase Phone OTP verification error:', error);
        return { 
          success: false, 
          error: error.message || 'Invalid OTP code'
        };
      }

      if (!data.user) {
        return { 
          success: false, 
          error: 'Verification failed. Please try again.' 
        };
      }

      console.log('✅ Supabase Phone OTP verified successfully:', data.user.phone);
      currentOtpProvider = null;

      return { 
        success: true, 
        user: data.user,
        session: data.session,
        message: 'Phone verified successfully'
      };
    } catch (error: any) {
      console.error('❌ Supabase Phone OTP verification error:', error);
      return { 
        success: false, 
        error: error.message || 'Failed to verify phone'
      };
    }
  },

  /**
   * Resend OTP with rate limiting check
   */
  async resendOtp(
    identifier: string, 
    type: OtpType
  ): Promise<OtpSendResult> {
    // Add a simple client-side rate limit tracker
    const lastSentKey = `otp_last_sent_${type}_${identifier}`;
    const lastSent = localStorage.getItem(lastSentKey);
    const now = Date.now();
    
    if (lastSent) {
      const timeSinceLastSent = now - parseInt(lastSent);
      const minWaitTime = 60000; // 60 seconds
      
      if (timeSinceLastSent < minWaitTime) {
        const remainingSeconds = Math.ceil((minWaitTime - timeSinceLastSent) / 1000);
        return { 
          success: false, 
          error: `Please wait ${remainingSeconds} seconds before requesting a new OTP` 
        };
      }
    }

    // Send OTP based on type
    const result = type === 'email' 
      ? await this.sendEmailOtp(identifier)
      : await this.sendPhoneOtp(identifier);

    if (result.success) {
      localStorage.setItem(lastSentKey, now.toString());
    }

    return result;
  },

  /**
   * Clear rate limit for a specific identifier
   */
  clearRateLimit(identifier: string, type: OtpType): void {
    localStorage.removeItem(`otp_last_sent_${type}_${identifier}`);
  },

  /**
   * Clear Firebase phone auth state
   * Call this when user changes phone number or cancels verification
   */
  clearPhoneAuthState(): void {
    confirmationResult = null;
    currentOtpProvider = null;
    if (recaptchaVerifier) {
      try {
        recaptchaVerifier.clear();
      } catch (e) {}
      recaptchaVerifier = null;
    }
    // Remove reCAPTCHA container if it exists
    const container = document.getElementById('recaptcha-container');
    if (container) {
      container.remove();
    }
    console.log('📱 Phone auth state cleared');
  }
};

export default otpService;

